// Full year/month/day age, not just a year subtraction (so a birthday later
// this month/year doesn't count as an extra year yet).
const ageInYears = (dateStr) => {
  const dob = new Date(dateStr);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
};

const NUMERIC_TYPES = ["number", "decimal", "currency"];

export function isFieldVisible(field, values) {
  if (field.field_show) {
    const [depName, allowedRaw] = field.field_show.split("/");
    if (values[depName] === undefined) return field.is_show !== false;
    const allowed = (allowedRaw || "").split("@");
    return allowed.includes(String(values[depName]));
  }
  return field.is_show !== false;
}

// A config-backed dropdown whose value isn't one of its loaded options (e.g. a
// saved 0 / label restored from get_lead_details) renders as "Select …", so it
// has to count as empty too — otherwise a required dropdown passes unseen.
const CONFIG_DD_TYPES = ["dd", "sdd"];
const hasUnknownOption = (field, val, configOptions) => {
  if (!CONFIG_DD_TYPES.includes(field.type)) return false;
  const options = (field.config && configOptions?.[field.config]) || field.options || [];
  return options.length > 0 && !options.some((o) => String(o.value) === val);
};

export function validateField(field, values, configOptions) {
  if (!field.name) return null;
  if (field.is_enable === false) return null;
  if (!isFieldVisible(field, values)) return null;

  const raw = values[field.name];
  let val = raw == null ? "" : String(raw);
  if (val.trim() !== "" && hasUnknownOption(field, val, configOptions)) val = "";
  // zero_as_empty: a 0 counts as "not entered" (e.g. optional Property Value).
  if (field.zero_as_empty && val.trim() !== "" && Number(val) === 0) val = "";

  if (field.is_required && val.trim() === "") return field.error_msg;
  if (val.trim() === "") return null;

  const rules = field.validation || {};
  if (rules.regex) {
    try {
      if (!new RegExp(rules.regex).test(val)) return field.error_msg;
    } catch (e) {
      /* malformed regex in schema, ignore */
    }
  }
  // On numeric fields the schema's min/max_length are value bounds (e.g. NOA
  // loan_term_years 1–30, interest_rate 0–100), not character counts; on dates
  // they're meaningless (approval_valid_until carries 0.3/1), so skip them.
  if (NUMERIC_TYPES.includes(field.type)) {
    const num = Number(val);
    if (Number.isNaN(num)) return field.error_msg;
    if (rules.min_length != null && num < rules.min_length) return field.error_msg;
    if (rules.max_length != null && num > rules.max_length) return field.error_msg;
  } else if (field.type !== "date") {
    if (rules.min_length != null && val.length < rules.min_length) return field.error_msg;
    if (rules.max_length != null && val.length > rules.max_length) return field.error_msg;
  }
  // A future date fails this the same way as being too young (age comes back
  // negative), so this alone also enforces "date of birth must be in the past".
  if (rules.min_age != null) {
    const age = ageInYears(val);
    if (age == null || age < rules.min_age) return field.error_msg;
  }

  return null;
}

export function validateFields(fields, values, configOptions) {
  const errors = {};
  fields.forEach((field) => {
    const msg = validateField(field, values, configOptions);
    if (msg) errors[field.name] = msg;
  });
  return errors;
}

export function buildFieldPayload(fields, values) {
  const payload = {};
  fields.forEach((field) => {
    if (field.name) payload[field.name] = values[field.name] ?? "";
  });
  return payload;
}

// Longest string a simple anchored pattern like "^9[0-9]{9}$" can match (10),
// so an input can stop typing where the regex would fail anyway. Only handles
// literals, \d-style escapes, [classes] and ?, {n}, {n,m} quantifiers; any
// pattern with groups, alternation or open-ended repeats (*, +, {n,}) gives
// undefined (no limit).
export function maxLengthFromRegex(regex) {
  if (!regex) return undefined;
  const src = String(regex).replace(/^\^/, "").replace(/\$$/, "");
  if (/[()|*+]/.test(src)) return undefined;
  let total = 0;
  let i = 0;
  while (i < src.length) {
    // one atom
    if (src[i] === "[") {
      const end = src.indexOf("]", i + 1);
      if (end === -1) return undefined;
      i = end + 1;
    } else if (src[i] === "\\") {
      i += 2;
    } else {
      i += 1;
    }
    // its quantifier
    let count = 1;
    if (src[i] === "?") {
      i += 1;
    } else if (src[i] === "{") {
      const end = src.indexOf("}", i);
      if (end === -1) return undefined;
      const [min, max] = src.slice(i + 1, end).split(",");
      if (max === "") return undefined;
      count = Number(max ?? min);
      if (Number.isNaN(count)) return undefined;
      i = end + 1;
    }
    total += count;
  }
  return total || undefined;
}

// Input maxLength for a text-like field: the schema's max_length, else what its regex allows.
export const inputMaxLength = (field) => field.validation?.max_length || maxLengthFromRegex(field.validation?.regex);

